import type { ReportTab } from '../hooks/useReportsTabs';

interface ReportsTabsProps {
  activeTab: ReportTab;
  onTabChange: (tab: ReportTab) => void;
  isMultiLocation: boolean;
}

interface TabConfig {
  id: ReportTab;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const CalendarIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ChartIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const ShieldIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const GridIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);

const TABS: TabConfig[] = [
  {
    id: 'daily',
    label: 'Daily Operations',
    description: 'Receiving & patient tracking',
    icon: <CalendarIcon />,
  },
  {
    id: 'performance',
    label: 'Performance',
    description: 'Metrics & analysis',
    icon: <ChartIcon />,
  },
  {
    id: 'compliance',
    label: 'Compliance',
    description: 'Exceptions & usage',
    icon: <ShieldIcon />,
  },
  {
    id: 'all',
    label: 'All Reports',
    description: 'Complete view',
    icon: <GridIcon />,
  },
];

export function ReportsTabs({ activeTab, onTabChange, isMultiLocation }: ReportsTabsProps) {
  return (
    <div className="mb-6">
      {/* Desktop tabs */}
      <div className="hidden sm:block">
        <nav className="flex space-x-1 bg-gray-100 p-1 rounded-lg" aria-label="Report tabs">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`
                  flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all
                  ${isActive 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }
                `}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className={isActive ? 'text-blue-600' : 'text-gray-400'}>
                  {tab.icon}
                </span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Mobile dropdown */}
      <div className="sm:hidden">
        <label htmlFor="report-tab-select" className="sr-only">
          Select a report category
        </label>
        <select
          id="report-tab-select"
          value={activeTab}
          onChange={(e) => onTabChange(e.target.value as ReportTab)}
          className="block w-full rounded-lg border-gray-300 py-2.5 pl-3 pr-10 text-base focus:border-blue-500 focus:outline-none focus:ring-blue-500"
        >
          {TABS.map((tab) => (
            <option key={tab.id} value={tab.id}>
              {tab.label} - {tab.description}
            </option>
          ))}
        </select>
      </div>

      {/* Tab description */}
      <div className="mt-3 text-sm text-gray-600">
        {activeTab === 'daily' && (
          <p>Daily receiving and patient order tracking for opticians.</p>
        )}
        {activeTab === 'performance' && (
          <p>
            {isMultiLocation 
              ? 'Compare performance across locations, manufacturers, and order efficiency.' 
              : 'Track your location\'s performance, turnaround times, and order efficiency.'
            }
          </p>
        )}
        {activeTab === 'compliance' && (
          <p>Review exceptions, aging items, and system usage compliance.</p>
        )}
        {activeTab === 'all' && (
          <p>View all reports in a single scrollable page.</p>
        )}
      </div>
    </div>
  );
}

